import React from "react";
import { apiClient } from "../../lib/apiClient";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Plus, Edit, Trash2, X, Upload } from "lucide-react";

export function AboutUsSectionManager() {
  const [sections, setSections] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [imageFile, setImageFile] = React.useState(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);

  // Form state for editing/creating
  const [formData, setFormData] = React.useState({
    title_en: "",
    title_uk: "",
    content_en: "",
    content_uk: "",
    display_order: 1,
    image_url: ""
  });

  React.useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/about-sections");
      setSections(response.data);
    } catch (err) {
      console.error("Failed to load sections:", err);
      setError("Не вдалося завантажити секції");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      title_en: "",
      title_uk: "",
      content_en: "",
      content_uk: "",
      display_order: sections.length + 1,
      image_url: ""
    });
    setImageFile(null);
    setCreating(true);
    setEditing(null);
  };

  const handleEdit = (section) => {
    setFormData({
      title_en: section.title_en,
      title_uk: section.title_uk,
      content_en: section.content_en,
      content_uk: section.content_uk,
      display_order: section.display_order,
      image_url: section.image_url
    });
    setImageFile(null);
    setEditing(section.id);
    setCreating(false);
  };

  const handleCancel = () => {
    setEditing(null);
    setCreating(false);
    setFormData({
      title_en: "",
      title_uk: "",
      content_en: "",
      content_uk: "",
      display_order: 1,
      image_url: ""
    });
    setImageFile(null);
  };

  const handleSave = async () => {
    try {
      setError(null);

      if (creating) {
        await apiClient.post("/admin/about-sections", formData);
        alert("Секцію успішно створено!");
      } else if (editing) {
        await apiClient.put(`/admin/about-sections/${editing}`, formData);
        alert("Секцію успішно оновлено!");
      }

      await fetchSections();
      handleCancel();
    } catch (err) {
      console.error("Failed to save section:", err);
      setError("Не вдалося зберегти секцію");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цю секцію?")) {
      return;
    }

    try {
      await apiClient.delete(`/admin/about-sections/${id}`);
      alert("Секцію успішно видалено!");
      await fetchSections();
    } catch (err) {
      console.error("Failed to delete section:", err);
      setError("Не вдалося видалити секцію");
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;

    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append("file", imageFile);

      const response = await apiClient.post("/admin/upload-image", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFormData({ ...formData, image_url: response.data.url });
      setImageFile(null);
      alert("Зображення успішно завантажено!");
    } catch (err) {
      console.error("Failed to upload image:", err);
      setError("Не вдалося завантажити зображення");
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
        <CardContent className="p-6">
          <p className="text-slate-400">Завантаження...</p>
        </CardContent>
      </Card>
    );
  }

  // Show editor if creating or editing
  if (creating || editing) {
    return (
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
        <CardContent className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-slate-50">
              {creating ? "Створити секцію" : "Редагувати секцію"}
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="rounded-full border-slate-700 text-slate-300"
              >
                СКАСУВАТИ
              </Button>
              <Button
                onClick={handleSave}
                className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
              >
                ЗБЕРЕГТИ
              </Button>
            </div>
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          {/* Image Upload */}
          <div className="space-y-3 rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
            <h3 className="text-sm font-semibold text-slate-300">Зображення секції</h3>
            
            {formData.image_url && (
              <div className="relative">
                <img 
                  src={formData.image_url} 
                  alt="Section" 
                  className="h-40 w-full rounded-lg object-cover"
                />
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0])}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:rounded-full file:border-0
                  file:bg-amber-500/10 file:px-4 file:py-2
                  file:text-xs file:font-semibold file:uppercase
                  file:tracking-wider file:text-amber-400
                  hover:file:bg-amber-500/20"
              />
              <Button
                size="sm"
                onClick={handleImageUpload}
                disabled={!imageFile || uploadingImage}
                className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
              >
                <Upload className="h-4 w-4 mr-1" />
                {uploadingImage ? "..." : "Завантажити"}
              </Button>
            </div>
          </div>

          <Field label="Порядок відображення">
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
            />
          </Field>

          <Field label="Заголовок (EN)">
            <input
              type="text"
              value={formData.title_en}
              onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
            />
          </Field>

          <Field label="Заголовок (UA)">
            <input
              type="text"
              value={formData.title_uk}
              onChange={(e) => setFormData({ ...formData, title_uk: e.target.value })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
            />
          </Field>

          <Field label="Контент (EN)">
            <div className="space-y-2">
              <textarea
                value={formData.content_en}
                onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                rows={8}
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
              />
              <p className="text-xs text-slate-400">
                💡 Підтримується HTML: <code className="text-amber-400">&lt;ul&gt;&lt;li&gt;</code>, <code className="text-amber-400">&lt;p&gt;</code>, <code className="text-amber-400">&lt;br&gt;</code>, <code className="text-amber-400">&lt;b&gt;</code>, <code className="text-amber-400">&lt;i&gt;</code>
              </p>
            </div>
          </Field>

          <Field label="Контент (UA)">
            <div className="space-y-2">
              <textarea
                value={formData.content_uk}
                onChange={(e) => setFormData({ ...formData, content_uk: e.target.value })}
                rows={8}
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
              />
              <p className="text-xs text-slate-400">
                💡 Підтримується HTML: <code className="text-amber-400">&lt;ul&gt;&lt;li&gt;</code>, <code className="text-amber-400">&lt;p&gt;</code>, <code className="text-amber-400">&lt;br&gt;</code>, <code className="text-amber-400">&lt;b&gt;</code>, <code className="text-amber-400">&lt;i&gt;</code>
              </p>
            </div>
          </Field>
        </CardContent>
      </Card>
    );
  }

  // Show list view
  return (
    <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
      <CardContent className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight text-slate-50">
              Сторінка "Про нас"
            </h1>
            <p className="text-sm text-slate-400">
              Керуйте секціями сторінки About Us
            </p>
          </div>
          <Button
            onClick={handleCreate}
            className="rounded-full bg-amber-500 px-4 text-slate-900 hover:bg-amber-400"
          >
            + СТВОРИТИ СЕКЦІЮ
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-center text-slate-400">Завантаження...</p>
        ) : sections.length === 0 ? (
          <p className="text-center text-slate-400">Секцій ще немає</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-slate-400">Порядок</TableHead>
                <TableHead className="text-slate-400">Зображення</TableHead>
                <TableHead className="text-slate-400">Заголовок (EN)</TableHead>
                <TableHead className="text-slate-400">Заголовок (UA)</TableHead>
                <TableHead className="text-slate-400 text-right">Дії</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sections.map((section) => (
                <TableRow key={section.id}>
                  <TableCell className="text-slate-300">{section.display_order}</TableCell>
                  <TableCell>
                    {section.image_url && (
                      <img
                        src={section.image_url}
                        alt={section.title_en}
                        className="h-12 w-16 rounded object-cover"
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-slate-300">{section.title_en}</TableCell>
                  <TableCell className="text-slate-300">{section.title_uk}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(section)}
                        className="text-slate-400 hover:text-slate-200"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(section.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5 text-sm">
      <div className="text-xs font-medium text-slate-300">{label}</div>
      {children}
    </div>
  );
}
